import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Helmet } from 'react-helmet-async'
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion"
import { Link } from "react-router-dom"
import { ArrowRight } from "lucide-react"

import { SEO } from "@/components/SEO"
import { comprehensiveFAQs } from "@/data/faqData"

// Generate FAQ Schema for all questions
const generateFAQSchema = () => {
    const allFaqs = [
        ...comprehensiveFAQs.general,
        ...comprehensiveFAQs.products,
        ...comprehensiveFAQs.services,
        ...comprehensiveFAQs.industries,
        ...comprehensiveFAQs.technology,
        ...comprehensiveFAQs.gettingStarted
    ];

    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": allFaqs.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };
};

const FAQ = () => {
    const faqSchema = generateFAQSchema();

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="FAQ | Knowledge Base | RSA Tech Group"
                description="Everything you need to know about RSA Tech Group's AI solutions, products, services, and delivery models. Get answers to common questions about enterprise AI transformation."
                image="https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?auto=format&fit=crop&w=800&q=80"
            />
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
            </Helmet>
            <Navigation />

            {/* Hero Section */}
            <section className="relative py-24 bg-navy-900 text-white overflow-hidden mt-10">
                <div className="absolute inset-0 bg-blue-900/10 pointer-events-none"></div>
                <div className="section-container relative z-10 text-center">
                    <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-2 block animate-fade-in">
                        Knowledge Base
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
                        Frequently Asked <span className="text-blue-500">Questions</span>
                    </h1>
                    <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
                        Everything you need to know about our approach, capabilities, products, and delivery models.
                    </p>
                </div>
            </section>

            {/* FAQ Content */}
            <section className="py-20 section-container">
                <div className="max-w-4xl mx-auto space-y-16">

                    {/* Category: General */}
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-navy-900 border-b border-gray-200 pb-3">About RSA Tech Group</h2>
                        <Accordion type="single" collapsible className="w-full space-y-2">
                            {comprehensiveFAQs.general.map((faq, idx) => (
                                <AccordionItem key={`gen-${idx}`} value={`gen-${idx}`} className="border border-gray-100 rounded-lg px-4">
                                    <AccordionTrigger className="text-lg font-medium text-left py-4 hover:text-blue-600">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-gray-600 text-base leading-relaxed pb-4">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>

                    {/* Category: Products */}
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-navy-900 border-b border-gray-200 pb-3">Products & Ecosystem</h2>
                        <Accordion type="single" collapsible className="w-full space-y-2">
                            {comprehensiveFAQs.products.map((faq, idx) => (
                                <AccordionItem key={`prod-${idx}`} value={`prod-${idx}`} className="border border-gray-100 rounded-lg px-4">
                                    <AccordionTrigger className="text-lg font-medium text-left py-4 hover:text-blue-600">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-gray-600 text-base leading-relaxed pb-4">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                        <div className="mt-6 text-center">
                            <Link to="/products" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium">
                                Explore our products <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    {/* Category: Services */}
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-navy-900 border-b border-gray-200 pb-3">Services & Delivery</h2>
                        <Accordion type="single" collapsible className="w-full space-y-2">
                            {comprehensiveFAQs.services.map((faq, idx) => (
                                <AccordionItem key={`svc-${idx}`} value={`svc-${idx}`} className="border border-gray-100 rounded-lg px-4">
                                    <AccordionTrigger className="text-lg font-medium text-left py-4 hover:text-blue-600">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-gray-600 text-base leading-relaxed pb-4">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                        <div className="mt-6 text-center">
                            <Link to="/what-we-do" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium">
                                Learn about our services <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    {/* Category: Industries */}
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-navy-900 border-b border-gray-200 pb-3">Industries & Compliance</h2>
                        <Accordion type="single" collapsible className="w-full space-y-2">
                            {comprehensiveFAQs.industries.map((faq, idx) => (
                                <AccordionItem key={`ind-${idx}`} value={`ind-${idx}`} className="border border-gray-100 rounded-lg px-4">
                                    <AccordionTrigger className="text-lg font-medium text-left py-4 hover:text-blue-600">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-gray-600 text-base leading-relaxed pb-4">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                        <div className="mt-6 text-center">
                            <Link to="/industries" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium">
                                Explore industry solutions <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    {/* Category: Technology */}
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-navy-900 border-b border-gray-200 pb-3">AI & Technology</h2>
                        <Accordion type="single" collapsible className="w-full space-y-2">
                            {comprehensiveFAQs.technology.map((faq, idx) => (
                                <AccordionItem key={`tech-${idx}`} value={`tech-${idx}`} className="border border-gray-100 rounded-lg px-4">
                                    <AccordionTrigger className="text-lg font-medium text-left py-4 hover:text-blue-600">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-gray-600 text-base leading-relaxed pb-4">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                        <div className="mt-6 text-center">
                            <Link to="/technologies" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium">
                                View our technology stack <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    {/* Category: Getting Started */}
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-navy-900 border-b border-gray-200 pb-3">Getting Started</h2>
                        <Accordion type="single" collapsible className="w-full space-y-2">
                            {comprehensiveFAQs.gettingStarted.map((faq, idx) => (
                                <AccordionItem key={`start-${idx}`} value={`start-${idx}`} className="border border-gray-100 rounded-lg px-4">
                                    <AccordionTrigger className="text-lg font-medium text-left py-4 hover:text-blue-600">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-gray-600 text-base leading-relaxed pb-4">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>

                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="section-container text-center">
                    <h2 className="text-3xl font-bold mb-4 text-gray-900">Still have questions?</h2>
                    <p className="text-gray-600 mb-8 max-w-xl mx-auto">
                        Our team is here to help. Schedule a consultation to discuss your specific needs.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:brightness-110 shadow-lg transition-all"
                    >
                        Contact Us <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default FAQ
